import { createContext, useContext, ReactNode, useState, useEffect, useMemo, useCallback } from "react";
import { authService } from "@/services/Auth/auth.service";
import { DecodedUserData } from "@/shared/types/auth.types";
import { Role } from "@/shared/constants/roles";
import { Permission } from "@/shared/constants/permissions";
import { logger } from "@/lib/logger";
import { STORAGE_KEYS } from "@/lib/security";
import { TOKEN_REFRESH_CHECK_INTERVAL_MS } from "@/shared/constants/app.constants";

interface UserContextType {
  user: DecodedUserData | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (role: Role) => boolean;
  hasAnyRole: (roles: Role[]) => boolean;
  hasAllRoles: (roles: Role[]) => boolean;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<DecodedUserData | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from token on mount
  useEffect(() => {
    const loadUser = () => {
      try {
        const userData = authService.decodeToken();
        if (userData) {
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        logger.error('Error loading user session:', error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Proactive token refresh - check every 60 seconds
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkAndRefreshToken = async () => {
      try {
        // Check if token is expiring soon (within 3 minutes)
        if (authService.isTokenExpiringSoon()) {
          logger.info('Token expiring soon, attempting refresh...');
          
          const newToken = await authService.refreshAccessToken();
          
          if (newToken) {
            // Update user data with new token
            const userData = authService.decodeToken();
            if (userData) {
              setUser(userData);
              logger.info('Token refreshed successfully');
            }
          } else {
            // Refresh failed, logout
            logger.warn('Token refresh failed, logging out');
            await logout();
          }
        }
      } catch (error) {
        logger.error('Error checking token expiration:', error);
      }
    };

    // Check immediately on mount
    checkAndRefreshToken();

    // Then check every minute
    const interval = setInterval(checkAndRefreshToken, TOKEN_REFRESH_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isAuthenticated, logout]);

  // Listen for storage events from other tabs
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // If refresh token was removed in another tab, logout this tab
      if (event.key === STORAGE_KEYS.REFRESH_TOKEN && event.newValue === null) {
        void logout();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [logout]);

  const login = useCallback(async (
    email: string, 
    password: string, 
    rememberMe: boolean = false
  ): Promise<void> => {
    try {
      const response = await authService.login(email, password, rememberMe);
      
      // Decode the token to get user data
      const userData = authService.decodeToken();
      
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        throw new Error('Failed to decode user data from token');
      }
    } catch (error) {
      logger.error('Login failed:', error);
      setUser(null);
      setIsAuthenticated(false);
      throw error;
    }
  }, []);



  // Role checking functions
  const hasRole = useCallback((role: Role): boolean => {
    return authService.hasRole(role);
  }, []);

  const hasAnyRole = useCallback((roles: Role[]): boolean => {
    return authService.hasAnyRole(roles);
  }, []);

  const hasAllRoles = useCallback((roles: Role[]): boolean => {
    return authService.hasAllRoles(roles);
  }, []);

  // Permission checking functions
  const hasPermission = useCallback((permission: Permission): boolean => {
    return authService.hasPermission(permission);
  }, []);

  const hasAnyPermission = useCallback((permissions: Permission[]): boolean => {
    return authService.hasAnyPermission(permissions);
  }, []);

  const hasAllPermissions = useCallback((permissions: Permission[]): boolean => {
    return authService.hasAllPermissions(permissions);
  }, []);

  const value = useMemo(() => ({ 
    user, 
    isAuthenticated, 
    isLoading,
    login, 
    logout,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  }), [
    user, 
    isAuthenticated, 
    isLoading,
    login, 
    logout,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  ]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}

export { UserProvider, useUser };
export type { UserContextType };
